import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { esignatureService } from "../../services/esignature";
import { getApiEndpoint } from "../../config/config";
import DocumentViewerModal from "../Recruiting/DocumentViewerModal";
import { IconFileText } from "@tabler/icons-react";
import {
  Box,
  Button,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Badge,
} from "@mantine/core";
import "./OrganizationContent.css";

const getRecipientDisplayName = recipient => {
  if (!recipient) return "";
  if (recipient.full_name) return recipient.full_name;
  if (recipient.name) return recipient.name;
  if (recipient.first_name || recipient.last_name) {
    return `${recipient.first_name || ""} ${recipient.last_name || ""}`.trim();
  }
  if (recipient.lead) {
    if (recipient.lead.first_name || recipient.lead.last_name) {
      return `${recipient.lead.first_name || ""} ${recipient.lead.last_name || ""}`.trim();
    }
    if (recipient.lead.name) return recipient.lead.name;
  }
  if (recipient.user) {
    if (recipient.user.first_name || recipient.user.last_name) {
      return `${recipient.user.first_name || ""} ${recipient.user.last_name || ""}`.trim();
    }
    if (recipient.user.name) return recipient.user.name;
  }
  return recipient.email || "Unknown";
};

function AdminTabDocumentsPage() {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();

  const [documentStats, setDocumentStats] = useState(null);
  const [loadingDocStats, setLoadingDocStats] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [docFilterStatus, setDocFilterStatus] = useState("all");
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const formatDate = dateString => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const fetchDocumentStats = async () => {
    try {
      setLoadingDocStats(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint("/v1/esignature/documents/stats/summary"),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setDocumentStats(result.data || null);
      } else {
        setDocumentStats(null);
      }
    } catch (error) {
      setDocumentStats(null);
    } finally {
      setLoadingDocStats(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoadingDocs(true);
      const jwt = await getVerifiedJWT();
      const status = docFilterStatus === "all" ? null : docFilterStatus;
      const result = await esignatureService.getDocuments(jwt, status);

      let documentsArray = [];
      if (Array.isArray(result)) {
        documentsArray = result;
      } else if (result && Array.isArray(result.data)) {
        documentsArray = result.data;
      } else if (result && Array.isArray(result.documents)) {
        documentsArray = result.documents;
      } else if (
        result &&
        result.data &&
        Array.isArray(result.data.documents)
      ) {
        documentsArray = result.data.documents;
      } else if (result && typeof result === "object") {
        const values = Object.values(result.data || result);
        if (values.length > 0 && values[0] && typeof values[0] === "object") {
          documentsArray = values;
        }
      }

      setDocuments(documentsArray);
    } catch (error) {
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchDocumentStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docFilterStatus]);

  return (
    <div className="org-section-content">
      <div className="carousel-section">
        <Group justify="space-between" mb="md">
          <div>
            <Title order={3}>Documents</Title>
            <Text c="dimmed" size="sm">
              E-Signature document states
            </Text>
          </div>
          <Button
            leftSection={<IconFileText size={14} />}
            onClick={() => navigate("/esignature/templates")}
            variant="default"
            size="md"
            radius="md"
            style={{
              backgroundColor: "white",
              border: "1px solid #dee2e6",
              color: "#2d3748",
              fontWeight: 500,
            }}
          >
            View All Templates
          </Button>
        </Group>

        {loadingDocStats ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Loading document statistics...
            </Text>
          </Group>
        ) : documentStats ? (
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="lg">
            <Paper
              withBorder
              p="lg"
              radius="md"
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                border:
                  docFilterStatus === "draft" ? "2px solid #868e96" : undefined,
                boxShadow:
                  docFilterStatus === "draft"
                    ? "0 4px 12px rgba(134, 142, 150, 0.15)"
                    : undefined,
              }}
              onClick={() => setDocFilterStatus("draft")}
            >
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Draft
              </Text>
              <Text size="2.5rem" fw={800} mt={8}>
                {documentStats.draft || 0}
              </Text>
              <Badge color="gray" variant="light" size="sm" mt={8}>
                In Progress
              </Badge>
            </Paper>

            <Paper
              withBorder
              p="lg"
              radius="md"
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                border:
                  docFilterStatus === "sent" ? "2px solid #228be6" : undefined,
                boxShadow:
                  docFilterStatus === "sent"
                    ? "0 4px 12px rgba(34, 139, 230, 0.15)"
                    : undefined,
              }}
              onClick={() => setDocFilterStatus("sent")}
            >
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Sent
              </Text>
              <Text size="2.5rem" fw={800} mt={8}>
                {documentStats.sent || 0}
              </Text>
              <Badge color="blue" variant="light" size="sm" mt={8}>
                Awaiting Signatures
              </Badge>
            </Paper>

            <Paper
              withBorder
              p="lg"
              radius="md"
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease",
                border:
                  docFilterStatus === "completed"
                    ? "2px solid #40c057"
                    : undefined,
                boxShadow:
                  docFilterStatus === "completed"
                    ? "0 4px 12px rgba(64, 192, 87, 0.15)"
                    : undefined,
              }}
              onClick={() => setDocFilterStatus("completed")}
            >
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                Completed
              </Text>
              <Text size="2.5rem" fw={800} mt={8}>
                {documentStats.completed || 0}
              </Text>
              <Badge color="green" variant="light" size="sm" mt={8}>
                Done
              </Badge>
            </Paper>
          </SimpleGrid>
        ) : null}

        <Group gap="xs" mb="xl">
          <Button
            size="xs"
            variant={docFilterStatus === "all" ? "filled" : "light"}
            onClick={() => setDocFilterStatus("all")}
            style={
              docFilterStatus === "all"
                ? { backgroundColor: "#1e3a8a", color: "white" }
                : { backgroundColor: "#1e3a8a10", color: "#1e3a8a" }
            }
          >
            All
          </Button>
          <Button
            size="xs"
            variant={docFilterStatus === "draft" ? "filled" : "light"}
            color="gray"
            onClick={() => setDocFilterStatus("draft")}
          >
            Draft
          </Button>
          <Button
            size="xs"
            variant={docFilterStatus === "sent" ? "filled" : "light"}
            color="blue"
            onClick={() => setDocFilterStatus("sent")}
          >
            Sent
          </Button>
          <Button
            size="xs"
            variant={docFilterStatus === "completed" ? "filled" : "light"}
            color="green"
            onClick={() => setDocFilterStatus("completed")}
          >
            Completed
          </Button>
        </Group>

        {loadingDocs ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Loading documents...
            </Text>
          </Group>
        ) : documents.length === 0 ? (
          <Text c="dimmed" size="sm" ta="center" py="xl" fs="italic">
            No documents found
          </Text>
        ) : (
          <Box>
            <Box
              style={{
                background: "white",
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.2fr 1.2fr 1fr 1fr",
                  gap: "12px",
                  padding: "12px 16px",
                  background: "#f8f9fa",
                  borderBottom: "2px solid #e0e0e0",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#495057",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                <div>Document Title</div>
                <div>Direct Seller</div>
                <div>Guarantor</div>
                <div style={{ textAlign: "center" }}>Status</div>
                <div>Date Sent</div>
              </Box>

              <Stack gap={0}>
                {documents.slice(0, 5).map((doc, index) => {
                  let directSeller = doc.recipients?.find(
                    r =>
                      r.recipient_type === "direct_seller" ||
                      r.name?.toLowerCase().includes("seller") ||
                      r.email?.toLowerCase().includes("seller")
                  );

                  let guarantor = doc.recipients?.find(
                    r =>
                      r.recipient_type === "guarantor" ||
                      r.name?.toLowerCase().includes("guarantor") ||
                      r.email?.toLowerCase().includes("guarantor")
                  );

                  if (
                    !directSeller &&
                    !guarantor &&
                    doc.recipients?.length > 0
                  ) {
                    const allRecipients = doc.recipients.filter(
                      r => r.role === "signer" || !r.role
                    );
                    if (allRecipients.length > 0) {
                      directSeller = allRecipients[0];
                      if (allRecipients.length > 1) {
                        guarantor = allRecipients[1];
                      }
                    }
                  }

                  return (
                    <Box
                      key={doc.id}
                      onClick={() => {
                        setSelectedDocument(doc);
                        setShowViewerModal(true);
                      }}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1.2fr 1.2fr 1fr 1fr",
                        gap: "12px",
                        padding: "14px 16px",
                        borderBottom:
                          index < documents.slice(0, 5).length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        transition: "all 0.15s ease",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                        <IconFileText
                          size={18}
                          style={{ color: "#1e3a8a", flexShrink: 0 }}
                        />
                        <Text size="sm" fw={500} lineClamp={1}>
                          {doc.title}
                        </Text>
                      </Group>

                      <div style={{ minWidth: 0, flex: 1 }}>
                        {directSeller ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "6px",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text
                                size="xs"
                                fw={500}
                                lineClamp={1}
                                style={{ marginBottom: "2px" }}
                              >
                                {getRecipientDisplayName(directSeller)}
                              </Text>
                              <Text size="10px" c="dimmed" lineClamp={1}>
                                {directSeller.email || "—"}
                              </Text>
                            </div>
                            {directSeller.has_signed && (
                              <Text
                                size="xs"
                                c="green"
                                style={{ flexShrink: 0 }}
                              >
                                ✓
                              </Text>
                            )}
                          </div>
                        ) : (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        )}
                      </div>

                      <div style={{ minWidth: 0, flex: 1 }}>
                        {guarantor ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "6px",
                            }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <Text
                                size="xs"
                                fw={500}
                                lineClamp={1}
                                style={{ marginBottom: "2px" }}
                              >
                                {getRecipientDisplayName(guarantor)}
                              </Text>
                              <Text size="10px" c="dimmed" lineClamp={1}>
                                {guarantor.email || "—"}
                              </Text>
                            </div>
                            {guarantor.has_signed && (
                              <Text
                                size="xs"
                                c="green"
                                style={{ flexShrink: 0 }}
                              >
                                ✓
                              </Text>
                            )}
                          </div>
                        ) : (
                          <Text size="xs" c="dimmed">
                            —
                          </Text>
                        )}
                      </div>

                      <div style={{ textAlign: "center" }}>
                        <Badge
                          size="sm"
                          variant="light"
                          color={
                            doc.status === "completed"
                              ? "green"
                              : doc.status === "sent"
                                ? "blue"
                                : "gray"
                          }
                        >
                          {doc.status === "in_progress"
                            ? "In Progress"
                            : doc.status}
                        </Badge>
                      </div>

                      <Text size="xs" c="dimmed">
                        {doc.sent_at ? formatDate(doc.sent_at) : "—"}
                      </Text>
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            {documents.length > 5 && (
              <Button
                variant="subtle"
                size="sm"
                onClick={() => navigate("/esignature")}
                style={{ alignSelf: "center", marginTop: "12px" }}
              >
                View all {documents.length} documents
              </Button>
            )}
          </Box>
        )}
      </div>

      {showViewerModal && selectedDocument && (
        <DocumentViewerModal
          isOpen={showViewerModal}
          onClose={() => {
            setShowViewerModal(false);
            setSelectedDocument(null);
          }}
          document={selectedDocument}
        />
      )}
    </div>
  );
}

export default AdminTabDocumentsPage;
